import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader, Field, Input, Modal, ModalFooter, Select, StatusPill, Textarea } from '~/components/ui';
import { supabase } from '~/lib/supabase';
import { useAuth } from '~/lib/auth';
import { formatDateTime } from '~/lib/format';
import { adminCreate, adminDelete } from '~/lib/api/admin';

export const Route = createFileRoute('/_app/admin/changelog')({
  component: ChangelogPage,
});

function ChangelogPage() {
  const qc = useQueryClient();
  const auth = useAuth();
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['changelog_entries'],
    queryFn: async () => {
      const { data } = await supabase
        .from('changelog_entries')
        .select('*')
        .order('shipped_at', { ascending: false })
        .limit(200);
      return data ?? [];
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminDelete('changelog_entries', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['changelog_entries'] }),
  });

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Updates</h1>
          <p className="text-sm text-[var(--color-ink-soft)] mt-1">
            Change log entries. Replaces the legacy <code>build-changelog.js</code> + <code>data/changelog.json</code> pipeline.
          </p>
        </div>
        {auth.hasRole('admin') && (
          <Button variant="primary" onClick={() => setCreating(true)}>+ Add update</Button>
        )}
      </div>

      <Card className="mt-6">
        <CardHeader>{(data ?? []).length} entries</CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="px-5 py-4 text-sm text-[var(--color-ink-muted)]">Loading…</div>
          ) : (data ?? []).length === 0 ? (
            <div className="px-5 py-4 text-sm text-[var(--color-ink-muted)]">No updates yet.</div>
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {(data ?? []).map((c) => (
                <li key={c.id} className="px-5 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-bold">{c.title}</span>
                    {c.area && <StatusPill kind="info">{c.area}</StatusPill>}
                    {c.category && <StatusPill kind="muted">{c.category}</StatusPill>}
                    <span className="text-xs text-[var(--color-ink-muted)] ml-auto">
                      {formatDateTime(c.shipped_at)}{c.author_name ? ` · ${c.author_name}` : ''}
                    </span>
                  </div>
                  {c.body && <div className="text-sm whitespace-pre-wrap mt-1">{c.body}</div>}
                  {auth.hasRole('admin') && (
                    <div className="text-right">
                      <Button size="sm" variant="ghost"
                        onClick={() => { if (confirm('Delete this entry?')) remove.mutate(c.id); }}>
                        Delete
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {creating && <ChangelogEditor onClose={() => setCreating(false)} />}
    </div>
  );
}

function ChangelogEditor({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const auth = useAuth();
  const [form, setForm] = useState({
    title: '',
    body: '',
    area: '',
    category: 'feature',
    shipped_at: new Date().toISOString().slice(0, 16),
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => adminCreate('changelog_entries', {
      title: form.title.trim(),
      body: form.body || null,
      area: form.area || null,
      category: form.category || null,
      shipped_at: new Date(form.shipped_at).toISOString(),
      source: 'manual',
      author_name: auth.profile?.display_name ?? auth.user?.email ?? null,
      is_published: true,
    }),
    onSuccess: (r) => {
      if (!r.ok) { setError(r.error ?? 'unknown'); return; }
      qc.invalidateQueries({ queryKey: ['changelog_entries'] });
      onClose();
    },
  });

  return (
    <Modal open onClose={onClose} title="New update" size="md">
      <form onSubmit={(e) => { e.preventDefault(); if (form.title.trim()) save.mutate(); }}>
        <div className="px-6 py-4 space-y-3">
          <Field label="Title" htmlFor="cl_t">
            <Input id="cl_t" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Body (markdown)" htmlFor="cl_b">
            <Textarea id="cl_b" value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Area" htmlFor="cl_a">
              <Input id="cl_a" value={form.area}
                onChange={(e) => setForm({ ...form, area: e.target.value })}
                placeholder="e.g. Operations" />
            </Field>
            <Field label="Category" htmlFor="cl_c">
              <Select id="cl_c" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="feature">feature</option>
                <option value="fix">fix</option>
                <option value="perf">perf</option>
                <option value="docs">docs</option>
                <option value="admin">admin</option>
              </Select>
            </Field>
            <Field label="Shipped at" htmlFor="cl_d">
              <Input id="cl_d" type="datetime-local" value={form.shipped_at}
                onChange={(e) => setForm({ ...form, shipped_at: e.target.value })} />
            </Field>
          </div>
          {error && <div className="text-sm text-[var(--color-danger)]">{error}</div>}
        </div>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
